import type { ErrorEvent } from '@sentry/react';

const INERTIA_SESSION_STORAGE_ERROR_MESSAGES = new Set([
  'The operation is insecure.',
  "Failed to read the 'sessionStorage' property from 'Window': Access is denied for this document.",
]);
const INERTIA_PAGE_RESOLUTION_ERROR_MESSAGES = new Set([
  "Cannot read properties of undefined (reading 'default')",
  "undefined is not an object (evaluating 'r.default')",
]);
const GOOGLE_IOS_INLINE_ERROR_MESSAGE = /^(?:Error: )?[a-zA-Z]{2}(?:`prod)?$/;

export const IGNORED_ERROR_PATTERNS = [
  'Load failed',
  'Network Error',
  'Request aborted',
  'timeout exceeded',
  /^Unable to preload CSS for /,
  /^Importing a module script failed\.?$/,
  /^Failed to fetch dynamically imported module: /,
  /^Invalid call to runtime\.sendMessage\(\)\. Tab not found\.$/,
];

export const shouldIgnoreSentryEvent = (event: ErrorEvent) => {
  const exception = event.exception?.values?.[0];
  const frames = exception?.stacktrace?.frames ?? [];

  if (!exception) return false;

  const isInertiaSessionStorageSecurityError =
    exception.type === 'SecurityError' &&
    INERTIA_SESSION_STORAGE_ERROR_MESSAGES.has(exception.value ?? '') &&
    frames.some(
      (frame) =>
        frame.filename?.includes('@inertiajs+core') === true ||
        frame.filename?.includes('/vite/assets/inertia-') === true,
    );
  const isInertiaPageResolutionError =
    exception.type === 'TypeError' &&
    INERTIA_PAGE_RESOLUTION_ERROR_MESSAGES.has(exception.value ?? '') &&
    frames.some(
      (frame) =>
        frame.filename?.includes('entrypoints/inertia') === true ||
        frame.filename?.includes('/vite/assets/inertia-') === true,
    );
  const isTranslatedDomMutationError =
    exception.type === 'NotFoundError' &&
    exception.value === 'The object can not be found here.' &&
    frames.some((frame) => frame.function === 'insertBefore') &&
    event.breadcrumbs?.some(
      (breadcrumb) =>
        breadcrumb.category === 'ui.click' &&
        typeof breadcrumb.message === 'string' &&
        breadcrumb.message.includes('font > font'),
    ) === true;
  const isInjectedCircularJsonError =
    exception.type === 'TypeError' &&
    exception.value?.startsWith('Converting circular structure to JSON') === true &&
    frames.some((frame) => frame.function === 'JSON.stringify') &&
    frames.some((frame) => frame.function?.endsWith('.appendChild') === true);
  // Sentry derives contexts.browser server-side, so the client checks the user agent.
  const isChromeIos = navigator.userAgent.includes('CriOS');
  const isGoogleIos = /iPhone|iPad|iPod/.test(navigator.userAgent) && navigator.userAgent.includes('GSA/');
  const isGoogleIosInlineScript =
    (isChromeIos || isGoogleIos) && !frames.some((frame) => frame.filename?.includes('/vite/assets/'));
  const isTwoLetterError = exception.type === 'Error' && GOOGLE_IOS_INLINE_ERROR_MESSAGE.test(exception.value ?? '');
  const isRecursionError =
    exception.type === 'RangeError' && exception.value === 'Maximum call stack size exceeded.' && frames.length > 0;

  return (
    isInertiaSessionStorageSecurityError ||
    isInertiaPageResolutionError ||
    isTranslatedDomMutationError ||
    isInjectedCircularJsonError ||
    (isGoogleIosInlineScript && (isTwoLetterError || isRecursionError))
  );
};
